import { useCallback, useEffect, useMemo, useState } from 'react';

import { asIsoCountryCode2 } from '@rbilabs/intl';

import { SupportedLanguages, SupportedRegions } from '@rbi-ctg/frontend';
import useEffectOnUpdates from 'hooks/use-effect-on-updates';
import useEffectOnce from 'hooks/use-effect-once';
import { LaunchDarklyFlag, useFlag, useLDContext } from 'state/launchdarkly';
import { useLoggerContext } from 'state/logger/context';
import en from 'state/translations/en.json';
import { TLocalizationMessages } from 'types/i18n';
import { FALLBACK_ISO2, FALLBACK_ISO3 } from 'utils/constants';
import * as DatadogLogger from 'utils/datadog';
import { ISOs } from 'utils/form/constants';
import { TFormatDateFn, formatDate as formatDateDefault } from 'utils/i18n';
import { createFormatDate, getDateFnsLocale } from 'utils/i18n/date';
import {
  TFormatDistanceToNowFn,
  createFormatDistanceToNow,
  formatDistanceToNow as formatDistanceToNowOrig,
} from 'utils/i18n/distance';
import {
  TFormatDurationFn,
  createFormatDuration,
  formatDuration as formatDurationDefault,
} from 'utils/i18n/duration';
import { getLanguageOrDefault } from 'utils/i18n/language';
import LocalStorage, { StorageKeys } from 'utils/local-storage';

import { regionSpecificLocalStorageKeys } from './constants';
import { getMessagesForLanguage } from './get-messages-for-language';
import { inferSupportedLocale } from './infer-supported-locale';
import { inferLanguage } from './inferLanguage';
import { loadLanguage } from './load-language';
import { loadLocale } from './load-locale';
import { loadRegion } from './load-region';
import { IUserCurrentLocaleCache } from './types';

export * from './constants';

const LOCALE_SEPARATOR = '-';

const reloadWindow = () => document.location.reload();

function findSupportedRegion(requestedRegion: string): SupportedRegions {
  return asIsoCountryCode2(requestedRegion) || FALLBACK_ISO2;
}

function findSupportedLanguage(requestedLanguage: string): SupportedLanguages {
  return getLanguageOrDefault(requestedLanguage);
}

function hydrateInitialLocale(useDefault = false): string {
  // if native, uses loadLocale.app.tsx to hydrate based on env variable
  const { language, region } = loadLocale();

  const newLocale = inferSupportedLocale(
    language as SupportedLanguages,
    region as SupportedRegions,
    useDefault
  );

  return newLocale;
}

/*
 * This Region hook has a few specs to it:
 *
 * 1. It is preloaded with the users inferred region from domain or url params
 * 2. It allows the UI to update the region manually
 * 3. When the users intentionally changes the region, we store that in local storage.
 *    so that we can preload with that selected region in the future.
 * 4. The preloaded locale (language + region) must exist in our list of `SORTED_PROD_SUPPORTED_LOCALES` or fallback to a value on that list.
 * 5. If the user updates their browser region, we should update too.
 */
const useIntl = () => {
  const { updateUserLocale } = useLDContext();
  const { decorateLogger } = useLoggerContext();

  const isLocalizationDisabled = useFlag(LaunchDarklyFlag.DISABLE_LOCALIZATION);

  const [messages, setMessages] = useState<TLocalizationMessages>(en);
  const [locale, setLocale] = useState<string>(hydrateInitialLocale(isLocalizationDisabled));
  const [userCurrentLocale, setUserCurrentLocale] = useState<IUserCurrentLocaleCache | undefined>(
    undefined
  );
  const [language, region] = useMemo(() => locale.split(LOCALE_SEPARATOR), [locale]) as [
    SupportedLanguages,
    SupportedRegions,
  ];

  // Formatting functions with the current locale applied.
  const [formatDate, setFormatDate] = useState<TFormatDateFn>(() => formatDateDefault);
  const [formatDistanceToNow, setFormatDistanceToNow] = useState<TFormatDistanceToNowFn>(
    () => formatDistanceToNowOrig
  );
  const [formatDuration, setFormatDuration] = useState<TFormatDurationFn>(
    () => formatDurationDefault
  );

  const clearRegionSpecificStorage = useCallback(() => {
    // Wipe out region specific keys
    regionSpecificLocalStorageKeys.forEach(LocalStorage.removeItem);
  }, []);

  const setCurrentLocale = useCallback(
    (newLanguage: SupportedLanguages, newRegion: SupportedRegions, reloadPage: boolean = true) => {
      const newLocale = inferSupportedLocale(newLanguage, newRegion, isLocalizationDisabled);
      const [newLocaleLanguage, newLocaleRegion] = newLocale.split(LOCALE_SEPARATOR);

      LocalStorage.setItem(StorageKeys.LANGUAGE, newLocaleLanguage);
      LocalStorage.setItem(StorageKeys.REGION, newLocaleRegion);

      if (newRegion !== region) {
        // Wipe out region specific keys
        clearRegionSpecificStorage();
      }

      // todo: make sure the locale is valid so we dont end up with fr-US
      setLocale(newLocale);
      getMessagesForLanguage({
        language: newLocaleLanguage,
        country: newLocaleRegion,
      }).then(setMessages);
      updateUserLocale({ region: newRegion, language: newLanguage });
      // reload to refresh content that is pulled from Sanity - edge case
      // eg if user's language is english, but they click an external link to french static page
      if (reloadPage) {
        reloadWindow();
      }
    },
    [isLocalizationDisabled, region, clearRegionSpecificStorage, updateUserLocale]
  );

  useEffectOnce(() => {
    window.addEventListener('languagechange', () => {
      setCurrentLocale(loadLanguage(), loadRegion());
    });
  });

  useEffectOnce(() => {
    decorateLogger({ region });
  });

  // We track the user's locale (region + language) in logger errors
  // some errors might occur in specific langauges, so its crucial
  // to know all info on how to reproduce.
  useEffect(() => {
    DatadogLogger.addContext('page_locale', locale);
  }, [locale]);

  useEffect(() => {
    const initializeFormattingFunctions = async (localeRbi: string) => {
      const localeDateFns = await getDateFnsLocale(localeRbi);
      setFormatDate(() => createFormatDate({ locale: localeDateFns }));
      setFormatDistanceToNow(() => createFormatDistanceToNow({ locale: localeDateFns }));
      setFormatDuration(() => createFormatDuration({ locale: localeDateFns }));
    };

    initializeFormattingFunctions(locale);
  }, [locale]);

  useEffectOnce(() => {
    getMessagesForLanguage({ language, country: region }).then(setMessages);
  });

  // Make sure locale will update for logged-in users who have localization enabled
  // and also any time the isLocalization flag changes - including when it kicks in initially
  useEffectOnUpdates(() => {
    // remove the region from localStorage so that it doesn't override the domain's region
    localStorage.removeItem(StorageKeys.REGION);
    setCurrentLocale(loadLanguage(), loadRegion(), false);
  }, [isLocalizationDisabled]);

  return {
    region,
    feCountryCode: ISOs[region] || FALLBACK_ISO3,
    language,
    locale,
    inferLanguage,
    findSupportedRegion,
    clearRegionSpecificStorage,
    findSupportedLanguage,
    setCurrentLocale,
    setUserCurrentLocale,
    userCurrentLocale,
    messages,
    formatDate,
    formatDistanceToNow,
    formatDuration,
  };
};

export default useIntl;
